import { useEffect, useState } from 'react';
import {
  LifeBuoy, AlertCircle, CheckCircle2, RefreshCw, Plus, Send, ChevronLeft,
} from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';
import { portalGovernanceApi, type Ticket, type TicketStats } from '../../api/portalGovernance';
import { useAuth } from '../../hooks/useAuth';
import { formatDateTime } from '../../utils/formatTime';

const STATUS_TONE: Record<string, string> = {
  open: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  in_progress: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  waiting: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
  resolved: 'bg-green-500/10 text-green-600 dark:text-green-400',
  closed: 'bg-slate-500/10 text-slate-500',
};

const EMPTY = { subject: '', body: '', category: 'general', priority: 'normal' };

function TicketThread({ id, onBack }: { id: string; onBack: () => void }) {
  const { user } = useAuth();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try { setTicket(await portalGovernanceApi.ticket(id)); }
    catch (err: any) { setError(err?.response?.data?.detail || 'Could not load ticket'); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [id]);

  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true); setError('');
    try {
      await portalGovernanceApi.replyTicket(id, { body: reply.trim() });
      setReply('');
      await load();
    } catch (err: any) { setError(err?.response?.data?.detail || 'Could not send reply'); }
    finally { setBusy(false); }
  };

  const setStatus = async (status: string) => {
    setBusy(true); setError('');
    try { setTicket(await portalGovernanceApi.updateTicket(id, { status })); await load(); }
    catch (err: any) { setError(err?.response?.data?.detail || 'Could not update'); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="flex h-40 items-center justify-center"><LoadingSpinner size="lg" /></div>;
  if (!ticket) return <div className="text-sm text-red-500">{error}</div>;

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"><ChevronLeft size={15} />Back to tickets</button>
      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-semibold text-slate-950 dark:text-slate-100">{ticket.subject}</h2>
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_TONE[ticket.status] || 'bg-slate-500/10 text-slate-500'}`}>{ticket.status}</span>
            </div>
            <p className="mt-0.5 text-xs text-slate-500">{ticket.number} · {ticket.category} · {ticket.priority}</p>
          </div>
          {ticket.can_manage && (
            <select value={ticket.status} disabled={busy} onChange={e => setStatus(e.target.value)}
              className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
              {['open', 'in_progress', 'waiting', 'resolved', 'closed'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
        </div>
        {ticket.body && <p className="mt-3 whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">{ticket.body}</p>}
      </Card>

      <Card className="p-5">
        <h3 className="mb-3 text-sm font-semibold text-slate-950 dark:text-slate-100">Conversation</h3>
        {(ticket.messages || []).length === 0 && <p className="text-sm text-slate-500">No replies yet.</p>}
        <div className="space-y-3">
          {(ticket.messages || []).map(m => (
            <div key={m.id} className={`rounded-lg p-3 ${m.author_role === 'staff' ? 'bg-brand-500/5 dark:bg-brand-500/10' : 'bg-slate-100 dark:bg-slate-800'}`}>
              <div className="mb-1 flex items-center gap-2 text-xs text-slate-500">
                <span className="font-medium capitalize">{m.author_role}</span>
                {m.is_internal && <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] text-amber-700 dark:text-amber-400">internal</span>}
                <span>{m.created_at ? formatDateTime(m.created_at) : ''}</span>
              </div>
              <p className="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">{m.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 space-y-2">
          <textarea rows={3} value={reply} onChange={e => setReply(e.target.value)} placeholder="Write a reply…"
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
          <Button size="sm" onClick={send} disabled={busy || !reply.trim()}>{busy ? <LoadingSpinner size="sm" /> : <Send size={14} />}Send reply</Button>
        </div>
      </Card>
    </div>
  );
}

export default function TicketsApp() {
  const { user } = useAuth();
  const isOwner = Boolean(user?.is_owner);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [stats, setStats] = useState<TicketStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const data = isOwner ? await portalGovernanceApi.tickets() : await portalGovernanceApi.myTickets();
      setTickets(data.tickets); setStats(data.stats);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not load tickets.');
    } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, [isOwner]);

  const create = async () => {
    if (!form.subject.trim()) { setError('A subject is required.'); return; }
    setBusy(true); setError(''); setSuccess('');
    try {
      await portalGovernanceApi.createTicket({ subject: form.subject.trim(), body: form.body || undefined, category: form.category, priority: form.priority });
      setForm(EMPTY); setShowForm(false); setSuccess('Ticket submitted.');
      await load();
    } catch (err: any) { setError(err?.response?.data?.detail || 'Could not create ticket'); }
    finally { setBusy(false); }
  };

  if (openId) return <TicketThread id={openId} onBack={() => { setOpenId(null); void load(); }} />;

  if (loading) return <div className="flex h-40 items-center justify-center"><LoadingSpinner size="lg" /></div>;

  return (
    <div className="space-y-6">
      {error && <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400"><AlertCircle size={16} />{error}</div>}
      {success && <div className="flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 px-3 py-2.5 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/30 dark:text-green-400"><CheckCircle2 size={16} />{success}</div>}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><LifeBuoy size={18} className="text-slate-500" />
          <div><h2 className="text-base font-semibold text-slate-950 dark:text-slate-100">Support Tickets</h2>
          <p className="text-sm text-slate-500">{isOwner ? 'All customer tickets.' : 'Your support requests.'}</p></div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => load()} disabled={loading}><RefreshCw size={14} /></Button>
          <Button size="sm" onClick={() => setShowForm(v => !v)}><Plus size={14} />New Ticket</Button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Card className="p-3"><div className="text-xl font-bold text-slate-950 dark:text-slate-100">{stats.open}</div><div className="text-xs text-slate-500">Open</div></Card>
          <Card className="p-3"><div className="text-xl font-bold text-slate-950 dark:text-slate-100">{stats.in_progress}</div><div className="text-xs text-slate-500">In progress</div></Card>
          <Card className="p-3"><div className="text-xl font-bold text-slate-950 dark:text-slate-100">{stats.resolved}</div><div className="text-xs text-slate-500">Resolved</div></Card>
          <Card className="p-3"><div className="text-xl font-bold text-slate-950 dark:text-slate-100">{stats.total}</div><div className="text-xs text-slate-500">Total</div></Card>
        </div>
      )}

      {showForm && (
        <Card className="space-y-3 p-5">
          <h3 className="text-sm font-semibold text-slate-950 dark:text-slate-100">New ticket</h3>
          <input placeholder="Subject" value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
          <textarea rows={3} placeholder="Describe the issue" value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
          <div className="flex flex-wrap gap-2">
            <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
              {['general', 'billing', 'bug', 'feature', 'account'].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
              {['low', 'normal', 'high', 'urgent'].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <Button size="sm" onClick={create} disabled={busy}>{busy ? <LoadingSpinner size="sm" /> : <Send size={14} />}Submit</Button>
          </div>
        </Card>
      )}

      <Card className="p-0">
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {tickets.length === 0 && <p className="px-5 py-8 text-center text-sm text-slate-500">No tickets yet.</p>}
          {tickets.map(t => (
            <button key={t.id} onClick={() => setOpenId(t.id)} className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate font-medium text-slate-950 dark:text-slate-100">{t.subject}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_TONE[t.status] || 'bg-slate-500/10 text-slate-500'}`}>{t.status}</span>
                </div>
                <div className="text-xs text-slate-500">{t.number} · {t.category} · {t.created_at ? formatDateTime(t.created_at) : ''}</div>
              </div>
              <ChevronLeft size={16} className="shrink-0 rotate-180 text-slate-400" />
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}
